/* global window */
import Auth from '../Auth';
import { AuthAction, refreshToken, migrateClassicSession, cleanClassicToken } from 'Actions/auth';
import { setTokenExpire, logout } from 'Actions/auth';
import { supportLocalStorage } from 'Helpers/localStorage';
const preemptiveRefreshTime = 2 * 60 * 1000; // eslint-disable-line no-magic-numbers
const blockRequestTime = 10 * 1000; // eslint-disable-line no-magic-numbers
const storageLockMaxTime = 3 * 1000; // eslint-disable-line no-magic-numbers
const lockRetryTime = 1000; // eslint-disable-line no-magic-numbers
const MAX_FAILED_RETRY = 2;
const EXCLUDED_URL_LIST = [
    '/api/account',
    '/api/auth'
];

export default (trackingHandler, withLegionSessionMigration) => store => {
    let lockDeniedStart;
    let startRefresh;
    let refreshPromise = null;
    let sessionMigrationPromise = null;
    let request_numbers = 0;
    const retryRequestFailed = {};
    const dispatchSessionMigration = (classicToken, language, type) => {
        if (!sessionMigrationPromise) {
            sessionMigrationPromise = store.dispatch(migrateClassicSession(classicToken, language, type))
                .then(() => {
                    store.dispatch(cleanClassicToken());
                    sessionMigrationPromise = null;
                }).catch(() => {
                    sessionMigrationPromise = null;
                });
        }
    };
    const dispatchRefresh = () => {
        if (!refreshPromise) {
            refreshPromise = store.dispatch(refreshToken(withLegionSessionMigration)).then(() => {
                refreshPromise = null;
                trackingHandler('blocked_requests', {
                    blocked_time: Date.now() - startRefresh,
                    request_numbers
                });
                request_numbers = 0;
            }).catch(() => {
                refreshPromise = null;
            });
        }
    };
    const processAuthAction = (state, action, currentTime) => {
        const { api: { cookiesObject } = {}, user: { sessionMigration, tokenInfo } = {}} = state || {};

        // classic session migration
        if (sessionMigration && sessionMigration.classicToken && cookiesObject && !cookiesObject.migrated) {
            if (!sessionMigrationPromise) {
                dispatchSessionMigration(sessionMigration.classicToken, sessionMigration.language, sessionMigration.type);
            }

            return sessionMigrationPromise.then(() => {
                return store.dispatch(action.action);
            });
        }
        // refresh token
        else if (tokenInfo && tokenInfo.expire) {
            if (!refreshPromise && tokenInfo.expire < (currentTime + preemptiveRefreshTime)) {
                if (supportLocalStorage()) {
                    if (window.localStorage.getItem('refreshingToken') || lockDeniedStart + storageLockMaxTime > currentTime) {
                        lockDeniedStart = lockDeniedStart || Date.now();
                        return new Promise(resolve => {
                            setTimeout(() => {
                                resolve(store.dispatch(action));
                            }, lockRetryTime);
                        });
                    }
                    window.localStorage.setItem('refreshingToken', window.sessionStorage.getItem('uuid'));
                    if (window.localStorage.getItem('tokenExpire') && window.localStorage.getItem('tokenExpire') > state.user.tokenInfo.expire) {
                        store.dispatch(setTokenExpire(window.localStorage.getItem('tokenExpire')));
                        window.localStorage.removeItem('refreshingToken');
                        return store.dispatch(action);
                    }
                }
                dispatchRefresh();
                startRefresh = currentTime;
                trackingHandler('refresh_token', {
                    time_to_expire: state.user.tokenInfo.expire - startRefresh
                });
            }
            if (tokenInfo.expire < (currentTime + blockRequestTime)) {
                request_numbers++;
                return refreshPromise.then(() => {
                    return store.dispatch(action.action);
                });
            }
        }
        return store.dispatch(action.action);
    };

    return next => action => {
        if (action instanceof AuthAction) {
            const state = store.getState();
            const currentTime = Date.now();

            return processAuthAction(state, action, currentTime);
        }

        if (action.type === 'UNAUTHORIZED_FETCH') {
            if (!Auth.isLegionEnabled()) {
                return store.dispatch(logout());
            }
            const rejectedUrl = action.rejectedAction?.payload?.url;

            // refresh only allowed on legion and certain domains
            const excludedUrlsFromTokenRefresh = EXCLUDED_URL_LIST.find(url => rejectedUrl.includes(url));

            retryRequestFailed[rejectedUrl] = retryRequestFailed[rejectedUrl] === undefined ? 1 : retryRequestFailed[rejectedUrl] + 1;

            if (!excludedUrlsFromTokenRefresh) {
                dispatchRefresh();

                if (retryRequestFailed[rejectedUrl] === MAX_FAILED_RETRY) {
                    return store.dispatch(logout());
                }

                return refreshPromise.then(() => {
                    return store.dispatch(action.rejectedAction);
                }).catch(error => {
                    throw error;
                });
            }
            if (action.rejectedAction?.actions?.receivedWithError) {
                return store.dispatch(action.rejectedAction.actions.receivedWithError());
            }
        }

        // if failed request was ok after retry we reset the value;
        if (retryRequestFailed[action?.url] && action.type.includes('RESPONSE_' || 'RECEIVE_')) {
            delete retryRequestFailed[action?.url];
        }

        return next(action); // eslint-disable-line callback-return
    };
};
